/**
 * Third-Party Guard (TPG) — 第三方广告/脚本恶意行为监测（MVP：仅上报，不阻断）。
 *
 * 配置入口：window.__TPG_CONFIG__（优先）或 window.__NAV_MONITOR_CONFIG__（兼容旧名）
 * Liquid 注入：snippets/nav-redirect-monitor.liquid
 * 调试 API：window.__TPG__（别名 window.__NAV_MONITOR__）
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 一、前端配置 __TPG_CONFIG__
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * baseUrl           {string}   必填。日志 API 根地址（POST /api/log/ticket + /api/log）。
 *                            例：https://s-api-eu.roborock.com
 *                            兼容旧字段 endpoint（自动解析 origin）。
 * appId             {string}   应用标识，参与 bodyHash，默认 shopify-tpg。
 *
 * siteId            {string|number}  站点标识，写入 POST 外层。Liquid 中通常为 shop.id。
 *
 * shopDomain        {string}   店铺域名，写入 POST 外层。Liquid 中通常为 shop.domain。
 *
 * enabled           {boolean}  显式开关。
 *                            true  → 强制启用（忽略 URL 参数与 defaultEnabled）。
 *                            false → 强制禁用。
 *                            未设置 → 看 URL 与 defaultEnabled（见下方「启用逻辑」）。
 *
 * defaultEnabled    {boolean}  enabled 未显式为 true 时生效。
 *                            false 且 URL 无 tpg=1/navMonitor=1 → 不启用。
 *
 * hookNavigation    {boolean}  默认 true。Hook location.assign/replace/href、window.open、
 *                            Navigation API navigate、动态插入的 meta refresh。
 *                            false 时跳过上述导航类 hook（MutationObserver 中的 meta 亦跳过）。
 *
 * trackLinkClicks   {boolean}  默认 true。监听 document trusted click，记录最近一次
 *                            <a href> 点击用于归因；用户主动点的同 URL 跨域跳不上报。
 *                            false 时关闭归因，所有符合条件的导航都会入队。
 *
 * hookScriptCapture {boolean}  默认 true。对 watch 列表内脚本拉取/读取 body 并扫描高危模式。
 *                            false 时跳过 script.src setter、fetch/XHR hook、script DOM 观察。
 *
 * watchScriptHosts  {string[]} 监控的脚本 host 后缀列表，默认 ['dsspn.com']。
 *                            匹配规则：hostname === host 或 hostname 以 .host 结尾（不区分大小写）。
 *
 * watchScriptPathRe {string}   可选。脚本 URL 路径正则（不含 delimiters），与 host 组合过滤：
 *                            - 同时配置了 watchScriptHosts 与 watchScriptPathRe → host 且 path 均命中；
 *                            - 只配其一 → 命中该项即可；
 *                            - 均未配 path 且 watchScriptHosts 有值 → 仅看 host。
 *                            例：'\\/trk\\/pix\\.js' 匹配 /trk/pix.js。
 *
 * watchScriptUrls   {string[]} 可选。URL 子串白名单式匹配（resolved URL indexOf），
 *                            命中即 watch，与 host/path 规则并列，用于精确指定个别脚本地址。
 *
 * allowlist         {string[]} 可选。追加域名白名单（合并内置 DEFAULT + PRESET），
 *                            目标 host 在白名单内视为正常跳转，不上报导航类事件。
 *                            匹配规则同 watchScriptHosts（精确或后缀）。
 *
 * scriptBodyMaxLen  {number}   script.body 的 body 字段最大字符数，默认 12000；超出截断并设 bodyTruncated。
 *
 * flushDebounceMs   {number}   事件入队后批量上报 debounce，默认 5000ms。
 *                            导航类事件在跨域 endpoint 时会立即 flush（见上报策略）。
 *
 * dedupeMs          {number}   发送前去重时间窗，默认 1500ms。
 *                            同 dedupeKey 或同 to 的 location.* 重复事件会被丢弃。
 *
 * debug             {boolean}  true 时在 console 输出 [TPG] captured / sendBeacon / fetch 日志。
 *
 * ── 启用逻辑（enabled 未显式为 true 时）──
 *   URL 含 tpg=0 或 navMonitor=0 → 禁用；
 *   URL 含 tpg=1 或 navMonitor=1 → 启用；
 *   否则若 defaultEnabled === false → 禁用；否则启用。
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 二、上报请求 POST /api/log/ticket + POST /api/log（frontend-log-reporter.js）
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 每条 TPG 事件单独走 ticket 流程（ticket 一次性），依赖 FrontendLogReporter。
 * bodyHash 参与字段（stack 不参与）：appId, pageUrl, errorType, errorMessage,
 *   userAgent, deviceInfo, timestamp, retryCount。
 *
 * 标准字段映射：
 *   appId         — cfg.appId
 *   pageUrl       — 捕获时 entry.from
 *   errorType     — TPG.{type}，如 TPG.location.href / TPG.script.body
 *   errorMessage  — 可读摘要（from→to、scriptUrl 等）
 *   stack         — JSON：sid/siteId/shopDomain/ref/captureStack + compactEvent 全量
 *   timestamp     — entry.ts
 *
 * 自定义透传（不参与 bodyHash，mergeExtraFields）：
 *   sid, siteId, shopDomain, ref, to, toHost, tpgType 及 script.body 专有字段。
 *
 * 跨域 fetch credentials:'omit' + keepalive（beforeunload / pagehide 会 flush）。
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 三、events[] — 导航类事件（type 非 script.body）
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 捕获来源与 type 取值：
 *   location.href      — Location.prototype.href 赋值
 *   location.assign    — Location.prototype.assign(url)
 *   location.replace     — Location.prototype.replace(url)
 *   window.open          — global.open(url, target, features)
 *   meta.refresh         — 首屏已有或动态插入 <meta http-equiv="refresh" content="...">
 *   （Navigation API）上述 location.* 还可能带内部 method:'navigation-api'，但 compact 后不输出 method）
 *
 * 每条 event 字段：
 *
 *   ts            {number}   捕获时刻 Unix 毫秒。
 *   type          {string}   见上表。
 *   from          {string}   捕获时 location.href。
 *   to            {string}   解析后的绝对目标 URL（meta.refresh 从 content 解析 url=）。
 *   toHost        {string}   to 的 hostname（小写）。
 *   suspicious    {boolean}  固定 true（预留字段，表示命中可疑规则）。
 *   sid           {string}   与外层 sid 相同，会话 ID。
 *   visibility    {string}   document.visibilityState（visible / hidden 等）。
 *   stack         {string}   调用栈首行，最长 64 字符（Error().stack）。
 *   trigger       {string?}  实际上报时固定为 'script'（表示非用户 link 归因的脚本侧跳转）。
 *   target        {string?}  window.open 的 target 窗口名（仅 window.open）。
 *   content       {string?}  meta.refresh 的原始 content 属性（仅 meta.refresh）。
 *
 * 不入队 / 不上报条件（导航类）：
 *   - to 为空、'#'、仅 hash 变化（origin+pathname+search 相同）；
 *   - 目标为静态资源 URL（.js/.css/.png 等扩展名或 /assets/ 等路径）；
 *   - 目标 host 在白名单（当前页 host、roborock.com、shopify 系、广告/支付等 PRESET）；
 *   - javascript:/data:/vbscript:/blob: scheme → 视为危险，会上报（产品口径：与文档「过滤」不同，保留上报）；
 *   - trackLinkClicks 开启且 to 与最近一次 trusted <a> 点击 normalize 后相同 → link 归因跳过。
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 四、events[] — script.body 事件
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 触发：watchScriptHosts / watchScriptPathRe / watchScriptUrls 命中后，通过
 *   script 标签 DOM、script.src setter、fetch、XHR 拉取脚本内容并扫描。
 *
 * 高危正则 REDIRECT_IN_SCRIPT_RE（hasRedirectPattern）匹配：
 *   location.href|assign|replace、window.open、document.write、
 *   top.location、parent.location、meta http-equiv=refresh 等。
 *
 * 入队条件（满足其一）：
 *   hasRedirectPattern === true，或 fetchError 非空字符串。
 *   仅拉取成功但无高危模式 → 不入队、不上报。
 *
 * 每条 event 字段（除导航类共有字段外）：
 *
 *   scriptUrl         {string}   脚本绝对 URL（与 to 通常相同）。
 *   source            {string}   捕获渠道：
 *                              script.tag / script.existing — DOM 中的 <script src>
 *                              script.src — 通过 src 属性 setter 赋值
 *                              fetch — 全局 fetch hook
 *                              xhr — XMLHttpRequest hook
 *   status            {number}   HTTP 状态码；拉取失败时为 0。
 *   bodyLen           {number}   响应体原始长度（截断前）。
 *   body              {string}   响应体文本，最长 scriptBodyMaxLen。
 *   bodyTruncated     {boolean?} body 被截断时为 true。
 *   fetchError        {string?}  拉取/读体失败原因：
 *                              fetch_unavailable — 环境无 fetch
 *                              cors_or_network — CORS 或网络错误
 *                              body_read_failed — response.clone().text() 失败
 *   hasRedirectPattern {boolean?} body 命中高危正则时为 true。
 *
 * 同一 scriptUrl（resolved）只处理一次（reportedScriptUrls 去重）。
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 五、本地持久化与调试
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * sessionStorage：
 *   __nav_monitor_sid     — 会话 ID
 *   __nav_monitor_v       — 队列 schema 版本（当前 '6'，变更时清空 __nav_redirect_log_queue）
 *   __nav_redirect_log_queue — 未发送事件 JSON 数组，最多 MAX_BUFFER=20 条；页面加载 drain 重试
 *
 * window.__TPG_LAST__ / __NAV_MONITOR_LAST__ — 最近一条入队事件（调试用）。
 *
 * __TPG__ 方法：
 *   getRecentLogs()       — LOG_BUFFER 副本（最近入队，最多 20）
 *   getLastLog()          — 最后一条事件
 *   getStatus()           — endpoint、isCrossOrigin、hook 开关、buffer/pending 大小等
 *   selfTest(url?)        — 模拟 location.href 捕获，默认 https://example.com/tpg-self-test
 *   isReportableTarget / isPageNavigationUrl / isStaticAssetUrl / ...
 *   capture / captureScriptBody — 手动触发（调试）
 */
(function (global) {
  'use strict';

  if (global.__TPG_INSTALLED__) return;
  global.__TPG_INSTALLED__ = true;
  global.__NAV_MONITOR_INSTALLED__ = true;

  var cfg = global.__TPG_CONFIG__ || global.__NAV_MONITOR_CONFIG__ || {};
  var LOG_BASE_URL = resolveLogBaseUrl(cfg);

  if (!LOG_BASE_URL) return;
  if (cfg.enabled === false) return;
  if (cfg.enabled !== true) {
    var search = global.location.search || '';
    if (search.indexOf('tpg=0') !== -1 || search.indexOf('navMonitor=0') !== -1) return;
    if (
      search.indexOf('tpg=1') === -1 &&
      search.indexOf('navMonitor=1') === -1 &&
      cfg.defaultEnabled === false
    ) {
      return;
    }
  }

  var STORAGE_KEY = '__nav_redirect_log_queue';
  var SID_KEY = '__nav_monitor_sid';
  var VERSION_KEY = '__nav_monitor_v';
  var STORAGE_VERSION = '7';
  var STACK_PAYLOAD_MAX = 8000;
  var ERROR_MSG_MAX = 2000;
  var MAX_BUFFER = 20;
  var FLUSH_DEBOUNCE_MS = cfg.flushDebounceMs || 5000;
  var DEDUPE_MS = cfg.dedupeMs || 1500;
  var STACK_MAX_LEN = 64;
  var SCRIPT_BODY_MAX = cfg.scriptBodyMaxLen || 12000;
  var LOCATION_TYPES = /^location\./;
  var REDIRECT_IN_SCRIPT_RE =
    /location\.(?:href|assign|replace)|window\.open|document\.write|top\.location|parent\.location|meta\s+http-equiv\s*=\s*["']?refresh/i;
  var reportedScriptUrls = {};
  var ASSET_EXT_RE = /\.(js|mjs|cjs|css|json|map|woff2?|ttf|eot|otf|svg|png|jpe?g|gif|webp|ico|mp4|webm|mp3|wav|pdf|zip|wasm)(\?|#|$)/i;
  var ASSET_PATH_RE = /\/(assets|static|dist|build|chunks|vendor)\/[^/?#]+\.(js|css|mjs)(\?|#|$)/i;

  var DEFAULT_ALLOWLIST = ['roborock.com', 'shopify.com', 'shop.app', 'myshopify.com'];
  var PRESET_ALLOWLIST = [
    'shopifycdn.com',
    'account-eu.roborock.com',
    'account.roborock.com',
    'force.com',
    'salesforce.com',
    'site.com',
    'google.com',
    'googletagmanager.com',
    'google-analytics.com',
    'googleapis.com',
    'gstatic.com',
    'doubleclick.net',
    'facebook.com',
    'facebook.net',
    'connect.facebook.net',
    'hotjar.com',
    'visualwebsiteoptimizer.com',
    'vimeo.com',
    'youtube.com',
    'cloudflare.com',
    'paypal.com',
    'stripe.com',
    'klaviyo.com',
    'upsellit.com',
    'aimroas.com',
    'impact.com',
    'afterpay.com',
  ];

  try {
    if (sessionStorage.getItem(VERSION_KEY) !== STORAGE_VERSION) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    }
  } catch (e) {}

  var ALLOWLIST = mergeAllowlist(DEFAULT_ALLOWLIST, PRESET_ALLOWLIST, cfg.allowlist);

  var LOG_BUFFER = [];
  var PENDING_SEND = [];
  var SESSION_ID = getOrCreateSid();
  var flushTimer = null;
  var unloadFlushed = false;
  var lastLinkClick = null;
  var IS_CROSS_ORIGIN = false;
  var LOG_REPORTER = null;

  try {
    IS_CROSS_ORIGIN = new URL(LOG_BASE_URL).origin !== global.location.origin;
  } catch (e) {
    IS_CROSS_ORIGIN = true;
  }

  function resolveLogBaseUrl(config) {
    if (config.baseUrl) return String(config.baseUrl).replace(/\/+$/, '');
    var ep = config.endpoint || '';
    if (!ep) return '';
    try {
      return new URL(ep).origin;
    } catch (e) {
      return String(ep)
        .replace(/\/api\/(?:common\/nav-redirect\/log|log)\/?$/i, '')
        .replace(/\/+$/, '');
    }
  }

  function getLogReporter() {
    if (LOG_REPORTER) return LOG_REPORTER;
    if (!global.FrontendLogReporter) return null;
    LOG_REPORTER = global.FrontendLogReporter.create({
      appId: cfg.appId || 'shopify-tpg',
      baseUrl: LOG_BASE_URL,
      debug: cfg.debug,
      credentials: 'omit',
      keepalive: true,
    });
    return LOG_REPORTER;
  }

  function mergeAllowlist(defaults, preset, extra) {
    var seen = {};
    var result = [];
    function add(list) {
      if (!list || !list.length) return;
      for (var i = 0; i < list.length; i++) {
        var d = String(list[i]).toLowerCase();
        if (!seen[d]) {
          seen[d] = true;
          result.push(d);
        }
      }
    }
    add(defaults);
    add(preset);
    add(extra);
    return result;
  }

  function getOrCreateSid() {
    try {
      var sid = sessionStorage.getItem(SID_KEY);
      if (!sid) {
        sid =
          global.crypto && global.crypto.randomUUID
            ? global.crypto.randomUUID()
            : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
        sessionStorage.setItem(SID_KEY, sid);
      }
      return sid;
    } catch (e) {
      return String(Date.now());
    }
  }

  function captureStack() {
    try {
      var lines = (new Error().stack || '').split('\n');
      var caller = (lines[1] || lines[0] || '').trim();
      if (caller.length > STACK_MAX_LEN) {
        return caller.slice(0, STACK_MAX_LEN);
      }
      return caller;
    } catch (e) {
      return '';
    }
  }

  function resolveUrl(url) {
    try {
      return new URL(String(url).trim(), global.location.href).href;
    } catch (e) {
      return '';
    }
  }

  function normalizeNavUrl(url) {
    try {
      var u = new URL(String(url), global.location.href);
      u.hash = '';
      return u.href;
    } catch (e) {
      return resolveUrl(url);
    }
  }

  function resolveHost(url) {
    try {
      return new URL(String(url), global.location.href).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function isDangerousScheme(url) {
    return /^(javascript|data|vbscript|blob):/i.test(String(url || '').trim());
  }

  function isStaticAssetUrl(url) {
    var str = String(url || '').trim();
    if (!str) return false;
    if (isDangerousScheme(str)) return false;
    try {
      var u = new URL(str, global.location.href);
      if (ASSET_EXT_RE.test(u.pathname)) return true;
      if (ASSET_PATH_RE.test(u.pathname)) return true;
      return false;
    } catch (e) {
      return ASSET_EXT_RE.test(str);
    }
  }

  function isPageNavigationUrl(resolved) {
    if (!resolved) return false;
    if (isDangerousScheme(resolved)) return true;
    if (isStaticAssetUrl(resolved)) return false;
    try {
      var protocol = new URL(resolved).protocol;
      return protocol === 'http:' || protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  function isAllowlistedHost(host) {
    if (!host) return true;
    host = host.toLowerCase();
    var currentHost = global.location.hostname.toLowerCase();
    if (host === currentHost) return true;
    for (var i = 0; i < ALLOWLIST.length; i++) {
      var domain = ALLOWLIST[i];
      if (host === domain || host.slice(-domain.length - 1) === '.' + domain) {
        return true;
      }
    }
    return false;
  }

  function isHashOnlyChange(fromUrl, toUrl) {
    try {
      var from = new URL(fromUrl);
      var to = new URL(toUrl);
      return from.origin === to.origin && from.pathname === to.pathname && from.search === to.search;
    } catch (e) {
      return false;
    }
  }

  function parseMetaRefreshUrl(content) {
    if (!content) return '';
    var match = String(content).match(/url\s*=\s*(['"]?)([^'"\s;]+)\1/i);
    return match ? match[2] : '';
  }

  function isAttributableToLink(toUrl) {
    if (!lastLinkClick || cfg.trackLinkClicks === false) return false;
    var normalizedTo = normalizeNavUrl(toUrl);
    if (!normalizedTo) return false;
    return normalizedTo === lastLinkClick.href;
  }

  /** 是否值得上报：页面级跳转 + 可疑外域或危险 scheme */
  function isReportableTarget(rawUrl, fromUrl) {
    if (!rawUrl) return false;
    var trimmed = String(rawUrl).trim();
    if (!trimmed || trimmed === '#' || /^#/.test(trimmed)) return false;
    if (isDangerousScheme(trimmed)) return true;

    var resolved = resolveUrl(trimmed);
    if (!resolved) return false;
    if (!isPageNavigationUrl(resolved)) return false;
    if (fromUrl && isHashOnlyChange(fromUrl, resolved)) return false;

    var host = resolveHost(resolved);
    if (!host) return false;
    return !isAllowlistedHost(host);
  }

  function normalizeDetail(type, detail) {
    detail = detail || {};
    var raw = detail.to || detail.content || '';

    if (type === 'meta.refresh') {
      raw = parseMetaRefreshUrl(detail.content) || raw;
    }

    var to = raw ? resolveUrl(raw) : '';
    return {
      to: to,
      raw: raw,
      target: detail.target || '',
      content: detail.content || '',
      method: detail.method || '',
    };
  }

  function shouldCapture(type, detail) {
    var norm = normalizeDetail(type, detail || {});
    var raw = norm.raw || norm.to;

    if (!raw && type !== 'window.open') return false;

    if (type === 'window.open' && !raw) return false;

    return isReportableTarget(raw, global.location.href);
  }

  function buildEntry(type, detail) {
    var norm = normalizeDetail(type, detail || {});
    if (!isReportableTarget(norm.raw || norm.to, global.location.href)) return null;

    var entry = {
      ts: Date.now(),
      type: type,
      from: global.location.href,
      to: norm.to,
      toHost: resolveHost(norm.to || norm.raw),
      suspicious: true,
      sid: SESSION_ID,
      visibility: document.visibilityState || '',
      stack: captureStack(),
    };

    if (norm.target) entry.target = norm.target;
    if (norm.content) entry.content = norm.content;
    if (norm.method) entry.method = norm.method;

    return entry;
  }

  function eventDedupeKey(entry) {
    if (entry.type === 'script.body') {
      return 'script.body|' + (entry.scriptUrl || entry.to || '');
    }
    return (entry.to || entry.content || '') + '|' + entry.from;
  }

  function isWatchedScriptUrl(url) {
    if (!url) return false;
    var resolved = resolveUrl(url);
    if (!resolved) return false;

    var extra = cfg.watchScriptUrls;
    if (extra && extra.length) {
      for (var j = 0; j < extra.length; j++) {
        if (resolved.indexOf(String(extra[j])) !== -1) return true;
      }
    }

    var host = resolveHost(resolved);
    var hostMatch = false;
    var watchHosts = cfg.watchScriptHosts || ['dsspn.com'];
    for (var i = 0; i < watchHosts.length; i++) {
      var h = String(watchHosts[i]).toLowerCase();
      if (host === h || host.slice(-h.length - 1) === '.' + h) {
        hostMatch = true;
        break;
      }
    }

    var pathMatch = false;
    if (cfg.watchScriptPathRe) {
      try {
        pathMatch = new RegExp(cfg.watchScriptPathRe, 'i').test(resolved);
      } catch (e) {}
    } else {
      pathMatch = true;
    }

    if (watchHosts.length && cfg.watchScriptPathRe) {
      return hostMatch && pathMatch;
    }
    return hostMatch || pathMatch;
  }

  function buildScriptBodyEntry(url, source, body, status, fetchError) {
    var resolved = resolveUrl(url);
    var text = body == null ? '' : String(body);
    return {
      ts: Date.now(),
      type: 'script.body',
      from: global.location.href,
      to: resolved,
      toHost: resolveHost(resolved),
      scriptUrl: resolved,
      suspicious: true,
      sid: SESSION_ID,
      visibility: document.visibilityState || '',
      stack: captureStack(),
      source: source || '',
      status: status || 0,
      bodyLen: text.length,
      body: text.length > SCRIPT_BODY_MAX ? text.slice(0, SCRIPT_BODY_MAX) : text,
      bodyTruncated: text.length > SCRIPT_BODY_MAX,
      fetchError: fetchError || '',
      hasRedirectPattern: REDIRECT_IN_SCRIPT_RE.test(text),
    };
  }

  function captureScriptBody(url, source) {
    if (cfg.hookScriptCapture === false) return;
    if (!isWatchedScriptUrl(url)) return;

    var resolved = resolveUrl(url);
    if (!resolved || reportedScriptUrls[resolved]) return;
    reportedScriptUrls[resolved] = true;

    if (!global.fetch) {
      enqueue(buildScriptBodyEntry(resolved, source, '', 0, 'fetch_unavailable'));
      return;
    }

    global
      .fetch(resolved, {
        method: 'GET',
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
      })
      .then(function (res) {
        return res.text().then(function (text) {
          enqueue(buildScriptBodyEntry(resolved, source, text, res.status, ''));
        });
      })
      .catch(function () {
        enqueue(buildScriptBodyEntry(resolved, source, '', 0, 'cors_or_network'));
      });
  }

  function observeScriptElement(scriptEl, source) {
    if (!scriptEl) return;
    var src = scriptEl.src || scriptEl.getAttribute('src') || '';
    if (!src) return;
    captureScriptBody(src, source || 'script.tag');
  }

  function hookScriptSrcSetter() {
    try {
      var srcDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
      if (!srcDesc || !srcDesc.set) return;

      var nativeSet = srcDesc.set;
      Object.defineProperty(HTMLScriptElement.prototype, 'src', {
        get: srcDesc.get,
        set: function (value) {
          nativeSet.call(this, value);
          observeScriptElement(this, 'script.src');
        },
        configurable: true,
        enumerable: srcDesc.enumerable,
      });
    } catch (e) {}
  }

  function dedupeEvents(entries) {
    var result = [];

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var skip = false;

      for (var j = result.length - 1; j >= 0; j--) {
        var prev = result[j];
        if (entry.ts - prev.ts > DEDUPE_MS) break;

        if (eventDedupeKey(entry) === eventDedupeKey(prev)) {
          skip = true;
          break;
        }

        if (
          entry.to &&
          entry.to === prev.to &&
          LOCATION_TYPES.test(entry.type) &&
          LOCATION_TYPES.test(prev.type)
        ) {
          skip = true;
          break;
        }
      }

      if (!skip) result.push(entry);
    }

    return result;
  }

  function compactEvent(entry) {
    if (!entry || !entry.type) return null;
    if (entry.type === 'script.body') {
      var scriptOut = {
        ts: entry.ts,
        type: entry.type,
        from: entry.from || '',
        to: entry.to || entry.scriptUrl || '',
        toHost: entry.toHost || resolveHost(entry.scriptUrl || entry.to || ''),
        suspicious: true,
        sid: entry.sid || SESSION_ID,
        visibility: entry.visibility || '',
        stack: entry.stack || '',
        scriptUrl: entry.scriptUrl || entry.to || '',
        source: entry.source || '',
        status: entry.status || 0,
        bodyLen: entry.bodyLen || 0,
        body: entry.body || '',
      };
      if (entry.bodyTruncated) scriptOut.bodyTruncated = true;
      if (entry.fetchError) scriptOut.fetchError = entry.fetchError;
      if (entry.hasRedirectPattern) scriptOut.hasRedirectPattern = true;
      return scriptOut;
    }

    var out = {
      ts: entry.ts,
      type: entry.type,
      from: entry.from || '',
      to: entry.to || '',
      toHost: entry.toHost || resolveHost(entry.to || ''),
      suspicious: true,
      sid: entry.sid || SESSION_ID,
      visibility: entry.visibility || '',
      stack: entry.stack || '',
    };
    if (entry.trigger) out.trigger = entry.trigger;
    if (entry.target) out.target = entry.target;
    if (entry.content) out.content = entry.content;
    return out;
  }

  function scheduleFlush(immediate) {
    if (!PENDING_SEND.length) return;
    if (immediate) {
      clearTimeout(flushTimer);
      flushTimer = null;
      sendPending(false);
      return;
    }
    if (flushTimer) return;
    flushTimer = setTimeout(function () {
      flushTimer = null;
      sendPending(false);
    }, FLUSH_DEBOUNCE_MS);
  }

  function sendPending(isUnload) {
    if (isUnload) {
      if (unloadFlushed) return;
      unloadFlushed = true;
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    if (!PENDING_SEND.length) return;

    var batch = dedupeEvents(PENDING_SEND.splice(0, PENDING_SEND.length));
    if (!batch.length) return;
    flush(batch, isUnload);
  }

  function shouldEnqueueScriptBody(entry) {
    return entry.hasRedirectPattern || !!entry.fetchError;
  }

  function enqueue(entry) {
    if (entry.type === 'script.body' && !shouldEnqueueScriptBody(entry)) return;

    LOG_BUFFER.push(entry);
    if (LOG_BUFFER.length > MAX_BUFFER) LOG_BUFFER.shift();
    global.__TPG_LAST__ = entry;
    global.__NAV_MONITOR_LAST__ = entry;

    try {
      var queue = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      queue.push(entry);
      if (queue.length > MAX_BUFFER) queue = queue.slice(-MAX_BUFFER);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (e) {}

    PENDING_SEND.push(entry);
    if (IS_CROSS_ORIGIN && isNavigationEventType(entry.type)) {
      flushUrgent();
      return;
    }
    scheduleFlush(true);
  }

  function capture(type, detail) {
    if (!shouldCapture(type, detail)) return;

    var norm = normalizeDetail(type, detail || {});
    if (isAttributableToLink(norm.to || norm.raw)) return;

    var entry = buildEntry(type, detail);
    if (!entry) return;
    entry.trigger = 'script';
    enqueue(entry);
    if (cfg.debug) {
      try {
        global.console.info('[TPG] captured', type, entry.to || entry.toHost);
      } catch (e) {}
    }
  }

  function isNavigationEventType(type) {
    return type === 'meta.refresh' || type === 'window.open' || LOCATION_TYPES.test(type || '');
  }

  function buildErrorMessage(entry) {
    if (!entry) return '';
    if (entry.type === 'script.body') {
      return (
        '[TPG script.body] ' +
        (entry.scriptUrl || entry.to || '') +
        ' source=' +
        (entry.source || '') +
        ' status=' +
        (entry.status || 0) +
        (entry.hasRedirectPattern ? ' hasRedirectPattern' : '') +
        (entry.fetchError ? ' fetchError=' + entry.fetchError : '')
      );
    }
    var msg =
      '[TPG ' +
      (entry.type || 'unknown') +
      '] ' +
      (entry.from || '') +
      ' -> ' +
      (entry.to || '') +
      ' (host: ' +
      (entry.toHost || '') +
      ')';
    if (entry.trigger) msg += ' trigger=' + entry.trigger;
    if (entry.target) msg += ' target=' + entry.target;
    if (entry.content) msg += ' content=' + entry.content;
    if (msg.length > ERROR_MSG_MAX) return msg.slice(0, ERROR_MSG_MAX);
    return msg;
  }

  function buildStackPayload(entry) {
    if (!entry) return '';
    var compact = compactEvent(entry);
    if (!compact) return '';
    var stackObj = {
      sid: entry.sid || SESSION_ID,
      siteId: cfg.siteId || '',
      shopDomain: cfg.shopDomain || '',
      ref: document.referrer || '',
      sentAt: Date.now(),
      captureStack: entry.stack || '',
      event: compact,
    };
    var json = JSON.stringify(stackObj);
    if (json.length > STACK_PAYLOAD_MAX && compact.body) {
      var budget = STACK_PAYLOAD_MAX - 500;
      var overhead = JSON.stringify(
        Object.assign({}, stackObj, { event: Object.assign({}, compact, { body: '' }) })
      ).length;
      var bodyBudget = Math.max(0, budget - overhead);
      if (bodyBudget < compact.body.length) {
        compact.body = compact.body.slice(0, bodyBudget) + '...[truncated-for-stack]';
        compact.bodyTruncated = true;
        stackObj.event = compact;
        json = JSON.stringify(stackObj);
      }
    }
    if (json.length > STACK_PAYLOAD_MAX) {
      return json.slice(0, STACK_PAYLOAD_MAX);
    }
    return json;
  }

  function buildLogType(entry) {
    var type = 'TPG.' + (entry && entry.type ? entry.type : 'unknown');
    if (type.length > 64) return type.slice(0, 64);
    return type;
  }

  function eventToLogInput(entry) {
    if (!entry || !entry.type) return null;
    var compact = compactEvent(entry);
    if (!compact) return null;
    var input = {
      pageUrl: entry.from || global.location.href,
      errorType: buildLogType(entry),
      errorMessage: buildErrorMessage(entry),
      stack: buildStackPayload(entry),
      timestamp: entry.ts || Date.now(),
      sid: SESSION_ID,
      siteId: cfg.siteId || '',
      shopDomain: cfg.shopDomain || '',
      ref: document.referrer || '',
      to: entry.to || compact.to || '',
      toHost: entry.toHost || compact.toHost || '',
      tpgType: entry.type || '',
    };
    if (entry.type === 'script.body') {
      input.scriptUrl = compact.scriptUrl || '';
      input.source = compact.source || '';
      input.status = compact.status || 0;
      input.bodyLen = compact.bodyLen || 0;
      if (compact.bodyTruncated) input.bodyTruncated = true;
      if (compact.fetchError) input.fetchError = compact.fetchError;
      if (compact.hasRedirectPattern) input.hasRedirectPattern = true;
    }
    if (entry.trigger) input.trigger = entry.trigger;
    if (entry.target) input.target = entry.target;
    if (entry.content) input.content = entry.content;
    return input;
  }

  function postPayload(entries, urgent) {
    var reporter = getLogReporter();
    if (!reporter) {
      if (cfg.debug) {
        try {
          global.console.warn('[TPG] FrontendLogReporter unavailable, dropped', entries.length);
        } catch (e) {}
      }
      return false;
    }

    for (var i = 0; i < entries.length; i++) {
      (function (entry) {
        var input = eventToLogInput(entry);
        reporter
          .report(input, { maxRetries: urgent ? 0 : 1 })
          .then(function () {
            if (cfg.debug) {
              try {
                global.console.info('[TPG] log ok', input.errorType, input.to || input.scriptUrl);
              } catch (e) {}
            }
          })
          .catch(function (err) {
            if (cfg.debug) {
              try {
                global.console.warn('[TPG] log failed', err && err.message, input.errorType);
              } catch (e) {}
            }
          });
      })(entries[i]);
    }

    return true;
  }

  function flushUrgent() {
    if (!PENDING_SEND.length) return;
    var batch = dedupeEvents(PENDING_SEND.splice(0, PENDING_SEND.length));
    if (!batch.length) return;
    postPayload(batch, true);
  }

  function drainPersistedQueue() {
    try {
      var queue = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      if (!queue.length) return;
      sessionStorage.removeItem(STORAGE_KEY);

      for (var i = 0; i < queue.length; i++) {
        var item = queue[i];
        if (!item) continue;
        if (item.type === 'script.body' && item.scriptUrl) {
          if (item.hasRedirectPattern || item.fetchError) {
            PENDING_SEND.push(item);
          }
        } else if (item.to && isReportableTarget(item.to, item.from)) {
          PENDING_SEND.push(item);
        }
      }
      if (PENDING_SEND.length) scheduleFlush(true);
    } catch (e) {}
  }

  function flush(entries, isUnload) {
    if (!entries || !entries.length) return;
    postPayload(entries, !!isUnload);
  }

  function hookLocationMethod(name) {
    var original = Location.prototype[name];
    if (typeof original !== 'function') return;
    Location.prototype[name] = function (url) {
      capture('location.' + name, { to: url });
      return original.call(this, url);
    };
  }

  function installLinkClickTracking() {
    if (cfg.trackLinkClicks === false) return;

    function onClick(e) {
      if (!e.isTrusted) return;
      var target = e.target;
      if (!target || !target.closest) return;
      var anchor = target.closest('a[href]');
      if (!anchor) return;
      var href = anchor.getAttribute('href');
      if (!href || href === '#' || /^javascript:/i.test(href)) return;
      var resolved = resolveUrl(href);
      if (!resolved) return;
      lastLinkClick = {
        href: normalizeNavUrl(resolved),
        ts: Date.now(),
        toHost: resolveHost(resolved),
      };
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('click', onClick, true);
    }
  }

  function installNavigationApiListener() {
    if (cfg.hookNavigation === false) return;
    var nav = global.navigation;
    if (!nav || typeof nav.addEventListener !== 'function') return;

    nav.addEventListener('navigate', function (event) {
      try {
        if (event.downloadRequest != null) return;
        var dest = event.destination && event.destination.url;
        if (!dest) return;
        if (event.hashChange && isHashOnlyChange(global.location.href, dest)) return;
        var type = event.navigationType === 'replace' ? 'location.replace' : 'location.href';
        capture(type, { to: dest, method: 'navigation-api' });
      } catch (e) {}
    });
  }

  function installNavigationHooks() {
    if (cfg.hookNavigation === false) return;

    hookLocationMethod('assign');
    hookLocationMethod('replace');

    try {
      var hrefDesc = Object.getOwnPropertyDescriptor(Location.prototype, 'href');
      if (hrefDesc && hrefDesc.set) {
        var nativeHrefSet = hrefDesc.set;
        Object.defineProperty(Location.prototype, 'href', {
          get: hrefDesc.get,
          set: function (value) {
            capture('location.href', { to: value });
            nativeHrefSet.call(this, value);
          },
          configurable: true,
          enumerable: hrefDesc.enumerable,
        });
      }
    } catch (e) {}

    var nativeOpen = global.open;
    global.open = function (url, target, features) {
      if (url != null && url !== '') {
        capture('window.open', { to: url, target: target || '' });
      }
      return nativeOpen.call(global, url, target, features);
    };

    installNavigationApiListener();
  }

  function installScriptCaptureHooks() {
    if (cfg.hookScriptCapture === false) return;

    hookScriptSrcSetter();

    if (global.fetch) {
      var nativeFetch = global.fetch;
      global.fetch = function (input, init) {
        var url =
          typeof input === 'string' ? input : input && input.url ? input.url : '';
        var watched = isWatchedScriptUrl(url);
        return nativeFetch.apply(this, arguments).then(function (response) {
          if (watched && response && response.clone) {
            var resolved = resolveUrl(url);
            if (resolved && !reportedScriptUrls[resolved]) {
              reportedScriptUrls[resolved] = true;
              response
                .clone()
                .text()
                .then(function (text) {
                  enqueue(buildScriptBodyEntry(resolved, 'fetch', text, response.status, ''));
                })
                .catch(function () {
                  enqueue(
                    buildScriptBodyEntry(resolved, 'fetch', '', response.status, 'body_read_failed')
                  );
                });
            }
          }
          return response;
        });
      };
    }

    if (global.XMLHttpRequest) {
      var xhrOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__navMonitorXhrUrl = url;
        return xhrOpen.apply(this, arguments);
      };

      var xhrSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        var xhrUrl = xhr.__navMonitorXhrUrl;
        if (xhrUrl && isWatchedScriptUrl(xhrUrl)) {
          xhr.addEventListener('load', function () {
            var resolved = resolveUrl(xhrUrl);
            if (!resolved || reportedScriptUrls[resolved]) return;
            reportedScriptUrls[resolved] = true;
            enqueue(
              buildScriptBodyEntry(
                resolved,
                'xhr',
                xhr.responseText || '',
                xhr.status || 0,
                ''
              )
            );
          });
        }
        return xhrSend.apply(this, arguments);
      };
    }
  }

  function captureMetaRefreshElement(metaEl) {
    if (!metaEl) return;
    var httpEquiv = (metaEl.httpEquiv || metaEl.getAttribute('http-equiv') || '').toLowerCase();
    if (httpEquiv !== 'refresh') return;
    var content = metaEl.content || metaEl.getAttribute('content') || '';
    capture('meta.refresh', { content: content });
  }

  function scanExistingMetaRefresh() {
    if (cfg.hookNavigation === false || !document.querySelectorAll) return;
    var metas = document.querySelectorAll('meta[http-equiv]');
    for (var m = 0; m < metas.length; m++) {
      captureMetaRefreshElement(metas[m]);
    }
  }

  function installMutationObserver() {
    var hookNav = cfg.hookNavigation !== false;
    var hookScript = cfg.hookScriptCapture !== false;
    if (!hookNav && !hookScript) return;

    if (typeof MutationObserver === 'undefined') {
      if (hookNav) {
        if (document.documentElement) scanExistingMetaRefresh();
        else document.addEventListener('DOMContentLoaded', scanExistingMetaRefresh);
      }
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;

          if (hookNav && node.tagName === 'META') {
            captureMetaRefreshElement(node);
            return;
          }

          if (hookScript && node.tagName === 'SCRIPT') {
            observeScriptElement(node, 'script.tag');
            return;
          }

          if (hookScript && node.querySelectorAll) {
            var scripts = node.querySelectorAll('script[src]');
            for (var s = 0; s < scripts.length; s++) {
              observeScriptElement(scripts[s], 'script.tag');
            }
          }
        });
      });
    });

    function startObserve() {
      var root = document.documentElement;
      if (!root) return;
      observer.observe(root, { childList: true, subtree: true });
      if (hookNav) scanExistingMetaRefresh();
      if (hookScript) {
        var existing = document.querySelectorAll('script[src]');
        for (var i = 0; i < existing.length; i++) {
          observeScriptElement(existing[i], 'script.existing');
        }
      }
    }

    if (document.documentElement) startObserve();
    else {
      document.addEventListener('DOMContentLoaded', startObserve);
    }
  }

  installLinkClickTracking();
  installNavigationHooks();
  installScriptCaptureHooks();
  installMutationObserver();

  global.addEventListener('beforeunload', function () {
    sendPending(true);
  });

  global.addEventListener('pagehide', function () {
    sendPending(true);
  });

  global.__TPG__ = {
    getRecentLogs: function () {
      return LOG_BUFFER.slice();
    },
    getLastLog: function () {
      return global.__TPG_LAST__ || null;
    },
    /** 最近一条事件的 /api/log 请求体；无捕获记录时返回 null */
    getLastLogInput: function () {
      return eventToLogInput(global.__TPG_LAST__ || null);
    },
    /** 不依赖真实捕获，生成一条示例映射体供调试 bodyHash / ticket 流程 */
    getSampleLogInput: function (url) {
      url = url || 'https://example.com/tpg-self-test';
      return eventToLogInput({
        ts: Date.now(),
        type: 'location.href',
        from: global.location.href,
        to: resolveUrl(url),
        toHost: resolveHost(url),
        suspicious: true,
        sid: SESSION_ID,
        visibility: document.visibilityState || 'visible',
        stack: captureStack(),
        trigger: 'script',
      });
    },
    getStatus: function () {
      return {
        installed: true,
        baseUrl: LOG_BASE_URL,
        appId: cfg.appId || 'shopify-tpg',
        isCrossOrigin: IS_CROSS_ORIGIN,
        hasLogReporter: !!global.FrontendLogReporter,
        hookNavigation: cfg.hookNavigation !== false,
        navigationApi: !!(global.navigation && global.navigation.addEventListener),
        isTopFrame: global === global.top,
        lastLinkClick: lastLinkClick,
        bufferSize: LOG_BUFFER.length,
        pendingSize: PENDING_SEND.length,
      };
    },
    /** 不触发真实跳转，仅测试 capture → enqueue 链路 */
    selfTest: function (url) {
      url = url || 'https://example.com/tpg-self-test';
      var lenBefore = LOG_BUFFER.length;
      var result = {
        url: url,
        isTopFrame: global === global.top,
        reportable: isReportableTarget(url, global.location.href),
        linkAttributed: isAttributableToLink(url),
        captured: false,
      };
      capture('location.href', { to: url });
      result.captured = LOG_BUFFER.length > lenBefore;
      result.lastLog = global.__TPG_LAST__ || null;
      result.logs = LOG_BUFFER.slice();
      return result;
    },
    isReportableTarget: isReportableTarget,
    isPageNavigationUrl: isPageNavigationUrl,
    isStaticAssetUrl: isStaticAssetUrl,
    isWatchedScriptUrl: isWatchedScriptUrl,
    isAttributableToLink: isAttributableToLink,
    captureScriptBody: captureScriptBody,
    capture: capture,
    eventToLogInput: eventToLogInput,
  };
  global.__NAV_MONITOR__ = global.__TPG__;

  drainPersistedQueue();
})(window);

